
import React from 'react';
// @mui
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
// components
import Iconify from 'src/components/iconify';
import DarkModeButton from 'src/components/button/button-dark-model';
// types
import { CaySam, CaySamNhatKy } from 'src/types/caysam';

// ----------------------------------------------------------------------

type Props = {
  latestNhatKy: CaySamNhatKy | null;
  imageUrlTongQuan: string | null;
  imageUrlChiTiet: string | null;
  caySamData: CaySam | null;
  isInvestor: boolean;
  isLatestNhatKyInCurrentMonth: boolean;
  refreshKey: number;
  onAddNhatKy: VoidFunction;
  onEditNhatKy: VoidFunction;
  onOpenPreview: (url: string | null | undefined) => void;
  onOpenPdfDialog: VoidFunction;
};

export default function CaySamGrowthInfo({
  latestNhatKy,
  imageUrlTongQuan,
  imageUrlChiTiet,
  caySamData,
  isInvestor,
  isLatestNhatKyInCurrentMonth,
  refreshKey,
  onAddNhatKy,
  onEditNhatKy,
  onOpenPreview,
  onOpenPdfDialog,
}: Props) {
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          Thông tin sinh trưởng
        </Typography>
        {!isInvestor && (
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Iconify icon="solar:add-circle-bold" />}
              onClick={onAddNhatKy}
            >
              Thêm nhật ký
            </Button>
            {latestNhatKy && isLatestNhatKyInCurrentMonth && (
              <DarkModeButton
                variant="contained"
                size="small"
                startIcon={<Iconify icon="solar:pen-bold" />}
                onClick={onEditNhatKy}
              >
                Cập nhật nhật ký
              </DarkModeButton>
            )}
          </Stack>
        )}
      </Box>

      {/* Chỉ hiển thị thông tin sinh trưởng nếu có nhật ký trong tháng */}
      {latestNhatKy ? (
        <>
          <Stack direction="row" sx={{ pb: 2 }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Ảnh tổng quan
              </Typography>
              <Box
                sx={{
                  border: '1px solid #eee',
                  borderRadius: 2,
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#fafafa',
                  width: '100%',
                  height: 280,
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.9 },
                }}
                onClick={() => onOpenPreview(imageUrlTongQuan)}
              >
                <img
                  key={`tongquan-${refreshKey}-${latestNhatKy?.id || 'empty'}`}
                  src={imageUrlTongQuan || ''}
                  alt="Ảnh tổng quan"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Ảnh chi tiết
              </Typography>
              <Box
                sx={{
                  border: '1px solid #eee',
                  borderRadius: 2,
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#fafafa',
                  width: '100%',
                  height: 280,
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.9 },
                }}
                onClick={() => onOpenPreview(imageUrlChiTiet)}
              >
                <img
                  key={`chitiet-${refreshKey}-${latestNhatKy?.id || 'empty'}`}
                  src={imageUrlChiTiet || ''}
                  alt="Ảnh chi tiết"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            </Box>
          </Stack>

          {/* === KHỐI TÀI LIỆU ĐÍNH KÈM === */}
          <Box sx={{ mb: 3 }}>
            {/* Header cho phần tài liệu đính kèm */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                Tài liệu đính kèm
              </Typography>

              {/* NÚT THÊM PDF (Chỉ hiện nếu không phải investor) */}
              {!isInvestor && (
                <Button
                  size="small"
                  variant="soft"
                  color="info"
                  startIcon={<Iconify icon="eva:cloud-upload-fill" />}
                  onClick={onOpenPdfDialog}
                >
                  Thêm PDF
                </Button>
              )}
            </Stack>

            {caySamData?.caySam_DinhKems && caySamData.caySam_DinhKems.length > 0 ? (
              <Stack spacing={1}>
                {caySamData.caySam_DinhKems.map((dk) => (
                  <Stack
                    key={dk.idFile}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      bgcolor: 'background.neutral',
                      border: '1px dashed',
                      borderColor: 'divider',
                    }}
                  >
                    <Iconify
                      icon="solar:file-download-bold"
                      width={24}
                      sx={{ color: 'primary.main' }}
                    />

                    <Stack sx={{ flex: 1, minWidth: 0 }}>
                      <Link
                        href={dk.dinhKemFile}
                        target="_blank"
                        rel="noopener"
                        variant="body2"
                        noWrap
                        sx={{ fontWeight: 'medium' }}
                      >
                        {dk.dinhKemFile.split('/').pop() || 'Tài liệu đính kèm'}
                      </Link>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(dk.thoiGian).toLocaleDateString('vi-VN')}
                      </Typography>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            ) : (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontStyle: 'italic', display: 'block', mt: 1 }}
              >
                Chưa có tài liệu đính kèm.
              </Typography>
            )}
          </Box>
          {/* === HẾT KHỐI DANH SÁCH ĐÍNH KÈM === */}
        </>
      ) : (
        <Box
          sx={{
            p: 3,
            textAlign: 'center',
            color: 'text.secondary',
            bgcolor: '#f9f9f9',
            borderRadius: 2,
          }}
        >
          <Iconify
            icon="solar:calendar-mark-bold"
            sx={{ fontSize: 48, mb: 1, opacity: 0.5 }}
          />
          <Typography variant="body1" sx={{ mb: 0.5 }}>
            Chưa có nhật ký trong tháng này
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
            Vui lòng thêm nhật ký mới để xem thông tin sinh trưởng
          </Typography>
        </Box>
      )}
    </Box>
  );
}
